import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, UserPlus } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { YalolaLogo } from "@/components/YalolaLogo";
import { useBackHandler } from "@/lib/backButton";
import { LOGIN_REQUIRED_EVENT, authPath, stashReturnTo } from "@/lib/guestGate";

const REASONS = {
  react: ["guest.reason_react", "Réagir à une publication est réservé aux membres."],
  comment: ["guest.reason_comment", "Commenter est réservé aux membres."],
  save: ["guest.reason_save", "Enregistrer une publication est réservé aux membres."],
  follow: ["guest.reason_follow", "Suivre un profil ou une église est réservé aux membres."],
  donate: ["guest.reason_donate", "Soutenir un membre est réservé aux membres connectés."],
  attend: ["guest.reason_attend", "Participer à un événement est réservé aux membres."],
  generic: ["guest.reason_generic", "Cette action est réservée aux membres Yalola."],
};

// Mounted ONCE (App.js): the « Connecte-toi pour continuer » box every guest-gated action opens.
// A Radix Dialog so it nests cleanly over the post modal (clicks inside never close the post
// behind) and z-[130] so it also sits above the fullscreen photo/video viewers (z-100).
export const LoginPrompt = () => {
  const { user, t } = useApp();
  const nav = useNavigate();
  const [reason, setReason] = useState(null);

  useEffect(() => {
    const on = (e) => setReason(e?.detail?.reason || "generic");
    window.addEventListener(LOGIN_REQUIRED_EVENT, on);
    return () => window.removeEventListener(LOGIN_REQUIRED_EVENT, on);
  }, []);
  useEffect(() => { if (user) setReason(null); }, [user]);

  const open = !!reason && !user;
  useBackHandler(open, () => { setReason(null); return true; });

  const go = (tab) => { stashReturnTo(); setReason(null); nav(authPath(tab)); };
  const [rk, rd] = REASONS[reason] || REASONS.generic;
  const muted = { color: "var(--yalola-text-muted)" };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setReason(null); }}>
      <DialogContent
        className="!z-[130] w-[calc(100%-2rem)] max-w-sm rounded-3xl p-6 gap-0 block [&_[data-dialog-close]]:hidden"
        overlayClassName="!z-[130] bg-black/60"
        style={{ paddingBottom: "calc(1.5rem + var(--sa-bottom, 0px))" }}
        data-testid="login-prompt"
        data-reason={reason || undefined}
      >
        <div className="flex items-center gap-3">
          <YalolaLogo size={44} />
          <div className="min-w-0">
            <DialogTitle className="font-display font-bold text-base leading-tight text-left" style={{ color: "var(--yalola-text)" }} data-testid="login-prompt-title">
              {t("guest.title", "Connecte-toi pour continuer")}
            </DialogTitle>
            <DialogDescription className="text-sm mt-0.5 leading-snug text-left" style={muted} data-testid="login-prompt-reason">{t(rk, rd)}</DialogDescription>
          </div>
        </div>
        <p className="text-xs mt-3" style={muted} data-testid="login-prompt-hint">{t("guest.join_hint", "Rejoindre Yalola est gratuit et ne prend qu'une minute.")}</p>
        <div className="mt-4 grid gap-2">
          <Button className="w-full rounded-full pill-primary h-11" onClick={() => go("login")} data-testid="login-prompt-login">
            <LogIn size={16} className="mr-2" /> {t("guest.login", "Se connecter")}
          </Button>
          <Button variant="outline" className="w-full rounded-full h-11" onClick={() => go("register")} data-testid="login-prompt-register">
            <UserPlus size={16} className="mr-2" /> {t("guest.register", "Créer un compte")}
          </Button>
          <button type="button" className="text-sm py-2 font-medium" style={muted} onClick={() => setReason(null)} data-testid="login-prompt-later">
            {t("guest.later", "Plus tard")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginPrompt;
